import type { MenuItemDto } from '@bldsoft/cappie/api/wbs'
import type { AppearanceModule } from '@setplex/tria-api'
import type {
  BaseIssue,
  BaseSchema,
  OptionalSchema,
  StringSchema,
} from 'valibot'
import type { ApiType, ApiUnion } from '../../../shared/schemas'

import * as v from 'valibot'
import { not_empty_array, safe_array } from '../../../shared/schemas'

/**
 * List of standard modules in the app,
 * which has been existing before appearance modules feature
 */
const STATIC_MODULES = [
  'home',
  'tv_channels',
  'movie',
  'tv_shows',
  'epg',
  'catchups',
  'events',
  'search',
  'my_list',
  'library',
  'settings',
]

/**
 * Map of static module identifiers from API to internal identifiers.
 * Only applied when module type is 'static'.
 */
const STATIC_IDENTIFIER_MAP: Record<string, string> = {
  my_list: 'library',
}

/**
 * List of modules, which should be omitted from the menu,
 * because they are handled in other places of the app
 */
const OMIT_MODULES = ['search', 'settings']

export const LeafModuleSchema = v.pipe(
  v.object({
    title: v.string(),
    localizedTitleKey: v.nullish(v.string(), undefined) as any as OptionalSchema<StringSchema<undefined>, undefined>, // prettier-ignore
    identifier: v.string(),

    // `iconName` is not validated (I think this is not necessary), but it is required to cast to proper union type
    iconName: v.nullish(v.string(), undefined) as any as OptionalSchema<ApiUnion<['activity', 'ball', 'bookmark', 'catchup', 'cubes', 'cup', 'discovery', 'food', 'game', 'hat', 'hearts', 'home', 'list', 'lock', 'music', 'podcast', 'puzzle', 'rocket', 'series', 'tv']>, undefined>, // prettier-ignore

    // `type` is validated in the next rawTransform step
    type: v.string() as any as OptionalSchema<ApiUnion<['static', 'dynamic']>, undefined>, // prettier-ignore
  }),
  v.rawTransform<any, AppearanceModule>(({ dataset, addIssue, NEVER }) => {
    const mod: AppearanceModule = dataset.value

    // standard module, which has static implementation
    if (STATIC_MODULES.includes(mod.identifier)) {
      // known module, unknown type, should be ignored
      if (mod.type !== 'static' && mod.type !== 'dynamic') {
        addIssue({ message: `Unknown module type: ${mod.type}` })
        return NEVER // FP-3604
      }

      // Convert API identifiers to internal identifiers for static modules only (e.g. my_list -> library)
      // For dynamic modules, keep the original identifier for API requests
      if (mod.type === 'static' && STATIC_IDENTIFIER_MAP[mod.identifier]) {
        return { ...mod, identifier: STATIC_IDENTIFIER_MAP[mod.identifier] }
      }

      return mod
    }

    // custom module, unknown type, should be ignored
    else {
      if (mod.type !== 'dynamic') {
        addIssue({ message: `Unknown module type: ${mod.type}` })
        return NEVER
      }
    }

    return mod
  })
) satisfies ApiType<MenuItemDto, AppearanceModule>

export const GroupModuleSchema = v.intersect([
  LeafModuleSchema,
  v.object({
    modules: modules(LeafModuleSchema),
  }),
]) satisfies ApiType<MenuItemDto, AppearanceModule>

export const ModulesSchema = modules(
  v.union([GroupModuleSchema, LeafModuleSchema])
) satisfies ApiType<MenuItemDto[] | undefined, AppearanceModule[] | undefined>

/**
 * Schema to safely handle array of modules,
 * any unknown or invalid module will be skipped with warning in console
 */
function modules<
  const TSchema extends BaseSchema<unknown, unknown, BaseIssue<unknown>>,
>(schema: TSchema) {
  return v.nullish(
    not_empty_array(
      v.pipe(
        safe_array(schema, (value, error) => {
          console.warn('Module', value, 'omitted:', error)
        }),

        // filter out module, because they has separate place in the app
        v.transform((arr) =>
          arr.filter((m: any) => !OMIT_MODULES.includes(m.identifier))
        )
      ),
      undefined
    ),
    undefined
  )
}
